import React, { useState, useEffect } from "react"

/**
 * Fetches program data from SQL database by ID
 * Gets user data about each program including module completion status
 * acts as cache for updates in real time
 * Used in program homepage as well as interactive view
 * @returns
 */
const useSingleProgramDB = (programId, firebase) => {
  const [programWithUserCompletion, setProgramWithUserCompletion] = useState([])
  const [isProgramLoading, setIsProgramLoading] = useState(true) // Loading is false if no programId
  const [error, setError] = useState(null)

  const [programData, setProgramData] = useState(null)
  const [programModuleList, setProgramModuleList] = useState([])
  const [schoolsWithAccess, setSchoolsWithAccess] = useState([])

  const completedModulesCount = programModuleList.filter((module) => module.status === "completed").length
  const totalModulesCount = programModuleList.length
  const programCompletionPercentage = totalModulesCount > 0 ? (completedModulesCount / totalModulesCount) * 100 : 0

  // mark the module as completed within the program context cached layer here so that updates to UI follow without API call
  const markCachedModuleStatusInProgram = (moduleId, status) => {
    setProgramModuleList((prevModuleList) =>
      prevModuleList.map((module) =>
        module.module_id === +moduleId
          ? { ...module, status: status }
          : module
      )
    )
  }

  useEffect(() => {
    if (!programId || !firebase) {
      setIsProgramLoading(false) // Stop loading if no programId or firebase connection
      return
    }

    let isMounted = true // Prevent state updates on unmounted components

    const fetchData = async () => {
      try {
        const res = await firebase?.getProgramByIdWithUserProgress(programId)

        if (isMounted) {
          const resProgramData = res?.data?.program
          const resProgramModuleList = res?.data?.modules.sort(
            (a, b) => resProgramData.module_order.indexOf(a.module_id) - resProgramData.module_order.indexOf(b.module_id)
          )
          const resSchoolsWithAccess = res?.data?.schoolsWithAccess || []

          setProgramData(resProgramData)
          setProgramModuleList(resProgramModuleList)
          setSchoolsWithAccess(resSchoolsWithAccess)
        }
      } catch (err) {
        if (isMounted) {
          setError(err)
        }
      } finally {
        if (isMounted) {
          setIsProgramLoading(false)
        }
      }
    }

    fetchData()

    return () => {
      isMounted = false // Cleanup function to prevent state updates after unmount
    }
  }, [programId, firebase])

  return { programModuleList, programData, isProgramLoading, programCompletionPercentage, error, markCachedModuleStatusInProgram, schoolsWithAccess }
}

export default useSingleProgramDB
